import { motion } from 'framer-motion';
import { Ruler, PencilRuler, ShieldCheck, HeartHandshake } from 'lucide-react';
import Section from '../layout/Section';
import siteContent from '../../data/siteContent.json';
// Image import removed 
// Wait, "imalat resimleri" is at root. Vite assets should be in public or imported from src if possible, or I can use absolute paths if they are in public. 
// I moved them to root. Vite handles imports from outside src differently? No, usually assets go to public or src/assets.
// I will assume for now I should move them to public to make them easily accessible or import them if I move them to src/assets.
// For now, I'll use placeholders or try to reference them if I move them.
// Let's use a Placeholder for now and I will move images to public/assets later.

// Icon mapping to link JSON string IDs to actual components
const iconMap = {
    design: Ruler,
    "3d": PencilRuler,
    material: ShieldCheck,
    delivery: HeartHandshake
};

const ValueCard = ({ icon: Icon, title, description, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay }}
        className="flex items-center gap-3 md:gap-4 text-left p-4 md:p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 border border-secondary-grey/20"
    >
        <div className="w-10 h-10 md:w-12 md:h-12 bg-secondary-cream rounded-full flex items-center justify-center text-primary-wood shrink-0">
            <Icon size={22} />
        </div>
        <div>
            <h3 className="font-heading font-bold text-base md:text-lg mb-1.5 sm:mb-2 text-primary-charcoal">{title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed max-w-none sm:max-w-[250px]">{description}</p>
        </div>
    </motion.div>
);

const About = () => {
    const { about } = siteContent;

    return (
        <Section id="about" background="white" className="py-16 md:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-24 items-center">
                {/* Text Side */}
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="text-primary-walnut font-bold tracking-widest uppercase text-sm mb-2 block">
                        {about.badge}
                    </span>
                    <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-charcoal mb-5 md:mb-6 leading-tight">
                        {about.title.split('Sanata')[0]} <br />
                        <span className="text-primary-walnut">Sanata Dönüştürüyoruz</span>
                    </h2>

                    {/* Dynamic Paragraphs */}
                    {about.description.map((paragraph, index) => (
                        <p key={index} className={`text-gray-600 leading-relaxed ${index === 0 ? 'text-base md:text-lg mb-5 md:mb-6' : 'mb-6 md:mb-8'}`}>
                            {paragraph}
                        </p>
                    ))}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                        {about.values.map((value, index) => (
                            <ValueCard
                                key={index}
                                icon={iconMap[value.id]}
                                title={value.title}
                                description={value.desc}
                                delay={0.1 * (index + 1)}
                            />
                        ))}
                    </div>
                </motion.div>

                {/* Image Grid Side */}
                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="relative"
                >
                    <div className="grid grid-cols-2 gap-3 md:gap-4">
                        <div className="space-y-3 md:space-y-4 translate-y-4 md:translate-y-8">
                            <img
                                src="/assets/images/ErdeMobilya-imalat-1.webp"
                                srcSet="/assets/images/ErdeMobilya-imalat-1-480.webp 480w, /assets/images/ErdeMobilya-imalat-1-960.webp 960w, /assets/images/ErdeMobilya-imalat-1.webp 1200w"
                                sizes="(min-width: 1024px) 50vw, 100vw"
                                alt="Mobilya Atölyesi"
                                width="900"
                                height="1200"
                                loading="lazy"
                                decoding="async"
                                className="rounded-lg shadow-lg w-full h-40 sm:h-48 md:h-64 object-cover hover:scale-105 transition-transform duration-500"
                            />
                            <img
                                src="/assets/images/ErdeMobilya-imalat-7.webp"
                                srcSet="/assets/images/ErdeMobilya-imalat-7-480.webp 480w, /assets/images/ErdeMobilya-imalat-7-960.webp 960w, /assets/images/ErdeMobilya-imalat-7.webp 1200w"
                                sizes="(min-width: 1024px) 50vw, 100vw"
                                alt="Mutfak Detayı"
                                width="900"
                                height="1200"
                                loading="lazy"
                                decoding="async"
                                className="rounded-lg shadow-lg w-full h-32 sm:h-40 md:h-48 object-cover hover:scale-105 transition-transform duration-500"
                            />
                        </div>
                        <div className="space-y-3 md:space-y-4">
                            <img
                                src="/assets/images/ErdeMobilya-imalat-15.webp"
                                srcSet="/assets/images/ErdeMobilya-imalat-15-480.webp 480w, /assets/images/ErdeMobilya-imalat-15-960.webp 960w, /assets/images/ErdeMobilya-imalat-15.webp 1200w"
                                sizes="(min-width: 1024px) 50vw, 100vw"
                                alt="Ahşap Oyma"
                                width="900"
                                height="1200"
                                loading="lazy"
                                decoding="async"
                                className="rounded-lg shadow-lg w-full h-32 sm:h-40 md:h-48 object-cover hover:scale-105 transition-transform duration-500"
                            />
                            <img
                                src="/assets/images/ErdeMobilya-imalat-6.webp"
                                srcSet="/assets/images/ErdeMobilya-imalat-6-480.webp 480w, /assets/images/ErdeMobilya-imalat-6-960.webp 960w, /assets/images/ErdeMobilya-imalat-6.webp 1200w"
                                sizes="(min-width: 1024px) 50vw, 100vw"
                                alt="Ofis Mobilyası"
                                width="900"
                                height="1200"
                                loading="lazy"
                                decoding="async"
                                className="rounded-lg shadow-lg w-full h-40 sm:h-48 md:h-64 object-cover hover:scale-105 transition-transform duration-500"
                            />
                        </div>
                    </div>
                    {/* Decorative Pattern */}
                    <div className="absolute -z-10 -top-10 -right-10 w-64 h-64 bg-secondary-beige/30 rounded-full blur-3xl"></div>
                    <div className="absolute -z-10 -bottom-10 -left-10 w-64 h-64 bg-accent-gold/20 rounded-full blur-3xl"></div>
                </motion.div>
            </div>
        </Section>
    );
};

export default About;
